import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { debounceTime, distinctUntilChanged, first, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { Availability } from '../../../../../models/availability';
import { CustomerProductPrice } from '../../../../../models/price';
import { Product, ProductDimension, ProductMultiDimension } from '../../../../../models/product';
import { AuthService } from '../../../../../services/auth/auth.service';
import { AvailabilityService } from '../../../../../services/availability/availability.service';
import { GungFlowService } from '../../../../../services/gung-flow/gung-flow.service';
import { PriceService } from '../../../../../services/price/price.service';
import { BaseViewConfigService, ProductViewType } from '../../../../../services/base-view-config/base-view-config.service';
import { ProductListConfigService } from '../../../../../services/product-list-config/product-list-config.service';
import { ProductListV2ConfigService } from '../../../../../services/product-list-v2-config/product-list-v2-config.service';
import { ProductService } from '../../../../../services/products/product.service';
import { GridViewProductCardV2 } from '../../../../../components/product-grid-view-v2/product-grid-view-v2.component';
import { DynamicPageEditorHelperService } from '../../../services/dynamic-page-editor-helper/dynamic-page-editor-helper.service';
import { DynamicPageDcBaseComponent } from '../dynamic-page-dc-base/dynamic-page-dc-base.component';

@Component({
  selector: 'lib-dynamic-page-dc-products-list',
  standalone: false,
  templateUrl: './dynamic-page-dc-products-list.component.html',
  styleUrl: './dynamic-page-dc-products-list.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class DynamicPageDcProductsListComponent extends DynamicPageDcBaseComponent implements OnInit {
  protected productService = inject(ProductService);
  protected baseViewConfigService = inject(BaseViewConfigService);
  protected productListConfigService = inject(ProductListConfigService);
  protected productListV2ConfigService = inject(ProductListV2ConfigService);
  protected priceService = inject(PriceService);
  protected availabilityService = inject(AvailabilityService);
  protected authService = inject(AuthService);
  protected gungFlowService = inject(GungFlowService);
  protected dynamicPageEditorHelperService = inject(DynamicPageEditorHelperService);
  protected productListConfig = inject(ProductListConfigService);

  listItems: { id: string; component: any; data: GridViewProductCardV2 }[] = [];
  defaultViewComponent: any;
  defaultViewData: Product[] = [];
  products: Product[];
  productToAdd: string;
  isSales = false;
  isAnonymous = true;
  includeAvailability = false;
  fields = {
    productsIds: []
  };

  search = (searchTerm$: Observable<string>) => {
    return searchTerm$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map(term =>
        this.products
          .filter(product => {
            let hasHitAllTerms = true;
            const queryTerms = term.split(' ');
            const terms = this.productListConfig.getSearchTerms(product);
            queryTerms.forEach(queryTerm => {
              const locatedTerm = terms.find(searchTerm => {
                return !!searchTerm && searchTerm.toUpperCase().indexOf(queryTerm.toUpperCase()) >= 0;
              });

              hasHitAllTerms = hasHitAllTerms && !!locatedTerm;
            });
            return hasHitAllTerms;
          })
          .slice(0, 10)
      )
    );
  };

  inputFormatter = (product: Product) => product.id;

  resultFormatter = (product: Product) => product.id + ' - ' + product.name;

  ngOnInit(): void {
    super.ngOnInit();

    this.productListConfig.getItems().pipe(
      map(products => {
        return products.flatMap(p => {
          if (p.productType === "multiDimension") {
            const mdp = p as ProductMultiDimension;
            const primaryDimensions = mdp.primaryDimension?.length
              ? mdp.primaryDimension
              : [{ id: '', name: '' } as ProductDimension];
            return primaryDimensions.flatMap(pd =>
              mdp.secondaryDimension.map(sd => ({
                ...p,
                id: mdp.modelId + pd.id + sd.id,
                name: [p.name, pd.name, sd.name].filter(part => !!part).join(' - '),
                primaryDimension: pd,
                secondaryDimension: sd
              }))
            );
          }
          return [p];
        });
      })
    ).subscribe(ps => this.products = ps);

    this.authService.getRoles().pipe(first()).subscribe(roles => {
      this.isSales = roles.filter(role => role.toUpperCase() === 'ADMIN' || role.toUpperCase() === 'SALES').length > 0;
      this.isAnonymous = roles.filter(role => role.toUpperCase() === 'ANONYMOUS').length > 0;
    });

    this.gungFlowService.getSelectedFlow().pipe(first()).subscribe(
      flow => { this.includeAvailability = flow.useAvailabilities || flow.requireAvailability; }
    );

    if (this.edit) {
      this.dynamicPageEditorHelperService.productIdsSubject.asObservable().subscribe(_ => {
        this.getProductsInfo();
      });
    } else {
      this.getProductsInfo();
    }
  }

  addProduct(product: Product) {
    this.element.fields.productsIds = [...this.element.fields.productsIds, product.id];
    this.dynamicPageEditorHelperService.productIdsSubject.next(1);
    setTimeout(() => this.productToAdd = '');
  }

  getProductsInfo() {
    const productIds: string[] = this.element.fields.productsIds;
    if (!productIds || productIds.length === 0) {
      return;
    }

    if (this.baseViewConfigService.productViewType !== ProductViewType.defaultView) {
      this.productService.getProductsByIds(productIds).pipe(
        first(),
        switchMap(products => forkJoin({
          products: of(products),
          prices: this.priceService.getCurrentCustomerPrices(productIds).pipe(first()),
          availabilities: this.includeAvailability ? this.availabilityService.getAvailabilities(productIds).pipe(first()) : of([])
        }))
      ).subscribe(({ products, prices, availabilities }) => {
        const productById = new Map(products.map(product => [product.id, product]));
        this.listItems = productIds
          .map(id => {
            const product = productById.get(id);
            if (!product) return null;
            const price = prices.find(p => p && p.productId === id) as CustomerProductPrice;
            const availability = (availabilities as Availability[]).find(a => a && a.productId === id) as Availability;
            const itemData: GridViewProductCardV2 = {
              product,
              price,
              availability,
              isSales: this.isSales,
              isAnonymous: this.isAnonymous,
              includeAvailability: this.includeAvailability
            };
            return {
              id,
              component: this.productListV2ConfigService.getProductGridCardComponent(itemData),
              data: itemData
            };
          })
          .filter(item => !!item);
      });
    } else {
      this.defaultViewComponent = this.productListConfigService.getLayouts()[0].getListItemComponent();
      this.productService.getProductsByIds(productIds).pipe(first()).subscribe(products => {
        this.defaultViewData = products;
      });
    }
  }

  deleteProduct(productId) {
    this.element.fields.productsIds = this.element.fields.productsIds.filter(id => id !== productId);
    this.dynamicPageEditorHelperService.productIdsSubject.next(1);
  }

  dropProduct(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.element.fields.productsIds, event.previousIndex, event.currentIndex);
    this.dynamicPageEditorHelperService.productIdsSubject.next(1);
  }
}
